/**
 * Service-Plan Prospects — Phase A + B of the Service-Plan Upsell add-on.
 *
 * Cross-references this workshop's recent customers against EMaC and surfaces
 * the ones who DO NOT yet have an active service plan (Phase A). Each row's
 * "Draft quote email" button opens a modal that calls Claude to generate a
 * personalised pitch the advisor can edit and send via IONOS (Phase B).
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ShieldCheck, AlertCircle, Loader2, RefreshCw,
  Sparkles, Mail, PoundSterling, Car, Calendar, X, Send, Wand2,
  CheckCircle2, XCircle,
} from "lucide-react";
import { BrandLink } from "@/components/Brand";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function relTime(iso) {
  if (!iso) return "";
  try {
    const ago = (Date.now() - new Date(iso).getTime()) / 1000;
    if (ago < 60) return "just now";
    if (ago < 3600) return `${Math.floor(ago / 60)}m ago`;
    if (ago < 86400) return `${Math.floor(ago / 3600)}h ago`;
    return `${Math.floor(ago / 86400)}d ago`;
  } catch { return ""; }
}

export default function ServicePlanProspectsPage() {
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const [offers, setOffers] = useState([]);
  const [drafting, setDrafting] = useState(null); // prospect being drafted

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const [r, o] = await Promise.all([
        api.get("/service-plan-prospects?days=90&limit=100"),
        api.get("/service-plan-prospects/offers").catch(() => ({ data: { offers: [] } })),
      ]);
      setState({ loading: false, data: r.data, error: "" });
      setOffers(o.data?.offers || []);
    } catch (e) {
      setState({ loading: false, data: null, error: e?.response?.data?.detail || "Couldn't load prospects" });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const { loading, data, error } = state;
  const offersByReg = Object.fromEntries((offers || []).map((o) => [o.vehicle_reg, o]));

  return (
    <div className="min-h-screen bg-background" data-testid="service-plan-prospects-page">
      <header className="border-b border-border bg-card/50 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <BrandLink />
            <span className="px-2 py-0.5 rounded-full bg-success/15 border border-success/40 text-success text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Service-plan upsell
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="btn-outline text-xs" data-testid="prospects-refresh-btn">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
            </button>
            <Link to="/dashboard" className="btn-outline text-xs" data-testid="prospects-back-link">
              <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div>
          <div className="label-overline">Service-plan cross-reference</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Customers without a service plan</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            We checked each of your recent customers against your service-plan provider's live agreement database. The list below shows everyone who's
            visited you in the last 90 days but does <strong className="text-foreground">not</strong> currently hold a service plan —
            ready for a personalised quote email.
          </p>
        </div>

        {loading && (
          <div className="text-center py-16 text-muted-foreground" data-testid="prospects-loading">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Checking each customer for a service plan — this can take 30–60 seconds…
          </div>
        )}

        {!loading && error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/[0.06] p-4 flex items-start gap-3" data-testid="prospects-error">
            <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div className="text-sm">{error}</div>
          </div>
        )}

        {!loading && data && data.configured === false && (
          <div className="rounded-lg border border-warning/40 bg-warning/[0.08] p-5 flex items-start gap-3" data-testid="prospects-emac-not-configured">
            <AlertCircle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
            <div className="text-sm leading-relaxed">
              <strong className="block mb-1">Service-plan lookup isn't configured for your workshop yet.</strong>
              Service-plan prospect detection needs a provider login. Contact ServiceSell PRO to enable this add-on.
            </div>
          </div>
        )}

        {!loading && data && data.configured && (
          <>
            <div className="grid grid-cols-3 gap-3" data-testid="prospects-stats">
              <StatTile label="Checked" value={data.checked || 0} icon={Car} colour="primary" />
              <StatTile label="Already on a plan" value={data.skipped_active || 0} icon={ShieldCheck} colour="success" />
              <StatTile label="Prospects" value={data.prospects?.length || 0} icon={Sparkles} colour="warning" />
            </div>

            <div className="rounded-xl border border-success/30 bg-success/[0.04] p-4 flex items-start gap-3" data-testid="prospects-pitch-callout">
              <PoundSterling className="w-5 h-5 text-success shrink-0 mt-0.5" />
              <div className="text-sm leading-snug">
                <strong className="block">£5 per plan sold via ServiceSell AI.</strong>
                Click <em>Draft quote email</em> on any row — AI writes a personalised pitch using the customer's
                car class, anchored to today's price for 2 years to protect them from oil-market inflation.
              </div>
            </div>

            {(data.prospects || []).length === 0 ? (
              <div className="rounded-lg border border-border bg-card p-10 text-center text-sm text-muted-foreground" data-testid="prospects-empty">
                <ShieldCheck className="w-8 h-8 text-success mx-auto mb-3" />
                <strong className="block text-foreground">Every recent customer is already on a service plan.</strong>
                <span>Nothing to upsell here. Solid retention — well done.</span>
              </div>
            ) : (
              <div className="rounded-lg border border-border bg-card overflow-hidden">
                <table className="w-full text-sm" data-testid="prospects-table">
                  <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="text-left px-4 py-2.5 font-bold">Customer</th>
                      <th className="text-left px-4 py-2.5 font-bold">Vehicle</th>
                      <th className="text-left px-4 py-2.5 font-bold">Last visit</th>
                      <th className="text-left px-4 py-2.5 font-bold">Status</th>
                      <th className="text-right px-4 py-2.5 font-bold">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.prospects.map((p) => {
                      const sent = offersByReg[p.vehicle_reg];
                      const canEmail = !!p.customer_email;
                      return (
                        <tr key={p.vehicle_reg} className="hover:bg-muted/20 transition-colors" data-testid={`prospect-row-${p.vehicle_reg}`}>
                          <td className="px-4 py-3 align-top">
                            <div className="font-semibold">{p.customer_name || <span className="text-muted-foreground">No name on file</span>}</div>
                            {p.customer_email
                              ? <div className="text-[11px] text-muted-foreground font-mono-plex truncate max-w-[180px]">{p.customer_email}</div>
                              : <div className="text-[11px] text-warning">No email on file</div>}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <div className="font-mono-plex font-bold">{p.vehicle_reg}</div>
                            {(p.vehicle_make || p.vehicle_model) && (
                              <div className="text-[11px] text-muted-foreground">{p.vehicle_make} {p.vehicle_model}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 align-top text-sm">{fmtDate(p.last_visit_at)}</td>
                          <td className="px-4 py-3 align-top text-sm">
                            {sent ? (
                              <SentStatusPill offer={sent} onChanged={load} />
                            ) : (
                              <span className="text-[11px] text-muted-foreground uppercase tracking-wider">Not pitched</span>
                            )}
                          </td>
                          <td className="px-4 py-3 align-top text-right">
                            <button
                              type="button"
                              disabled={!canEmail}
                              onClick={() => canEmail && setDrafting(p)}
                              title={canEmail ? "Generate AI-drafted pitch email" : "Add a customer email to enable"}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-bold transition-colors ${
                                canEmail
                                  ? "border-primary/30 bg-primary/5 text-primary hover:bg-primary/15"
                                  : "border-border bg-card text-muted-foreground opacity-60 cursor-not-allowed"
                              }`}
                              data-testid={`prospect-generate-${p.vehicle_reg}`}
                            >
                              <Wand2 className="w-3.5 h-3.5" /> {sent ? "Re-draft" : "Draft quote email"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>

      {drafting && (
        <DraftEmailModal
          prospect={drafting}
          onClose={() => setDrafting(null)}
          onSent={() => { setDrafting(null); load(); }}
        />
      )}
    </div>
  );
}

function StatTile({ label, value, icon: Icon, colour = "primary" }) {
  const accent = colour === "success" ? "text-success border-success/30 bg-success/[0.04]"
              : colour === "warning" ? "text-warning border-warning/30 bg-warning/[0.04]"
              : "text-primary border-primary/30 bg-primary/[0.04]";
  return (
    <div className={`rounded-lg border ${accent} p-3`}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider opacity-80">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-display font-black text-2xl mt-1.5">{value}</div>
    </div>
  );
}

function DraftEmailModal({ prospect, onClose, onSent }) {
  const [termYears, setTermYears] = useState(2);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [recipient, setRecipient] = useState(prospect.customer_email || "");
  const [monthly, setMonthly] = useState(null);

  const generate = useCallback(async (term) => {
    setGenerating(true);
    try {
      const r = await api.post("/service-plan-prospects/draft-email", {
        vehicle_reg: prospect.vehicle_reg,
        customer_name: prospect.customer_name,
        customer_email: prospect.customer_email,
        vehicle_make: prospect.vehicle_make,
        vehicle_model: prospect.vehicle_model,
        term_years: term,
      });
      setSubject(r.data.subject);
      setBody(r.data.body);
      setMonthly(r.data.monthly_price);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate the email");
    } finally { setGenerating(false); }
  }, [prospect]);

  // Auto-generate as soon as the modal opens
  useEffect(() => { generate(termYears); }, [generate, termYears]);

  const send = async () => {
    if (!recipient.trim()) { toast.error("Recipient email is required"); return; }
    setSending(true);
    try {
      await api.post("/service-plan-prospects/send-email", {
        vehicle_reg: prospect.vehicle_reg,
        customer_name: prospect.customer_name,
        recipient_email: recipient.trim(),
        subject: subject.trim(),
        body: body,
        term_years: termYears,
        monthly_price: monthly,
      });
      toast.success(`Pitch sent to ${recipient.trim()}`);
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the email");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4" data-testid="draft-email-modal">
      <div className="w-full max-w-2xl bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div>
            <div className="label-overline text-success">Service-plan pitch</div>
            <div className="font-display font-bold text-base mt-0.5">
              {prospect.customer_name || "Customer"} · <span className="font-mono-plex">{prospect.vehicle_reg}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-muted" data-testid="draft-email-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
            <div className="space-y-1">
              <label className="text-xs font-medium">Plan term</label>
              <div className="flex gap-2">
                {[2, 3].map((y) => (
                  <button key={y} type="button" onClick={() => setTermYears(y)}
                    className={`px-3 py-1.5 rounded-md border-2 text-xs font-bold transition-all ${
                      termYears === y ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40"
                    }`}
                    data-testid={`term-${y}y-btn`}>
                    {y} years
                  </button>
                ))}
              </div>
            </div>
            <button type="button" onClick={() => generate(termYears)} disabled={generating} className="btn-outline text-xs" data-testid="regenerate-btn">
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Re-generate
            </button>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium">To</label>
            <input type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} className="input-default font-mono-plex text-sm" data-testid="draft-email-to-input" />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium">Subject</label>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} className="input-default text-sm" data-testid="draft-email-subject-input" disabled={generating} />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium">Message</label>
            {generating ? (
              <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground" data-testid="draft-email-generating">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                Drafting your personalised pitch — this takes about 5 seconds…
              </div>
            ) : (
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={14}
                className="input-default text-sm font-mono-plex leading-relaxed whitespace-pre-wrap"
                data-testid="draft-email-body-textarea"
              />
            )}
          </div>

          {monthly && !generating && (
            <div className="text-[11px] text-muted-foreground">
              💷 Suggested monthly price baked into the draft: <strong className="text-foreground">£{monthly.toFixed(2)}</strong> for {termYears} years.
              Tweak any £ figure in the body before sending if you've quoted differently.
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border bg-muted/20">
          <button onClick={onClose} className="btn-outline text-sm" data-testid="draft-email-cancel-btn">Cancel</button>
          <button onClick={send} disabled={sending || generating || !body || !subject} className="btn-primary text-sm" data-testid="draft-email-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? "Sending…" : "Send pitch"}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Live status pill for a sent offer — shows pitched-ago time + lets the
 * advisor mark the outcome (sold/declined) which fires the £5 commission
 * book-keeping on the backend.
 */
function SentStatusPill({ offer, onChanged }) {
  const [busy, setBusy] = useState(false);
  const flip = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/service-plan-prospects/offers/${offer.id}`, { status });
      toast.success(status === "sold" ? "Marked sold — £5 credited this month" : `Marked ${status}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    } finally { setBusy(false); }
  };
  if (offer.status === "sold") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-success/20 text-success border border-success/40 text-[11px] font-bold uppercase tracking-wider" data-testid={`offer-sold-${offer.vehicle_reg}`}>
        <CheckCircle2 className="w-3 h-3" /> Sold {offer.sold_source === "emac_poll" ? "(auto)" : ""}
      </span>
    );
  }
  if (offer.status === "declined") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border text-[11px] font-bold uppercase tracking-wider" data-testid={`offer-declined-${offer.vehicle_reg}`}>
        <XCircle className="w-3 h-3" /> Declined
      </span>
    );
  }
  return (
    <div className="flex flex-col gap-1" data-testid={`offer-sent-${offer.vehicle_reg}`}>
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-success/15 text-success border border-success/30 text-[11px] font-bold uppercase tracking-wider w-fit">
        <Send className="w-3 h-3" /> Pitched {relTime(offer.sent_at)}
      </span>
      <div className="flex gap-1">
        <button type="button" disabled={busy} onClick={() => flip("sold")}
          className="text-[10px] font-bold uppercase tracking-wider text-success hover:underline disabled:opacity-50"
          data-testid={`offer-mark-sold-${offer.vehicle_reg}`}>
          Mark sold
        </button>
        <span className="text-[10px] text-muted-foreground">·</span>
        <button type="button" disabled={busy} onClick={() => flip("declined")}
          className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground hover:underline disabled:opacity-50"
          data-testid={`offer-mark-declined-${offer.vehicle_reg}`}>
          Declined
        </button>
      </div>
    </div>
  );
}

